import { ref, Ref } from 'vue'

interface UserwayConfig {
  enabled: boolean
  account?: string
  position?: string
  size?: string
  language?: string
  color?: string
  type?: string
  statement_text?: string
  statement_url?: string
  mobile?: string
}

interface UseUserwayReturn {
  isLoaded: Ref<boolean>
  config: Ref<UserwayConfig | null>
  error: Ref<string | null>
  loadUserwayConfig: (locale?: string) => Promise<void>
  reloadUserway: (locale?: string) => Promise<void>
}

declare global {
  interface Window {
    UserWayWidgetApp?: any
    UserWay?: {
      init?: () => void
      destroy?: () => void
      reload?: () => void
      updateConfig?: (config: any) => void
      [key: string]: any
    }
  }
}

export const useUserway = (): UseUserwayReturn => {
  const isLoaded = ref<boolean>(false)
  const config = ref<UserwayConfig | null>(null)
  const error = ref<string | null>(null)

  // Get Strapi API URL from environment variables
  const STRAPI_URL = import.meta.env.VITE_API_URL || 'http://localhost:1337'

  const loadUserwayConfig = async (locale: string = 'en'): Promise<void> => {
    try {
      // Clear any existing UserWay configuration
      if (window.UserWayWidgetApp) {
        delete window.UserWayWidgetApp
        isLoaded.value = false
      }

      const response = await fetch(`${STRAPI_URL}/api/settings/userway?locale=${locale}`)
      
      if (!response.ok) {
        throw new Error(`Failed to fetch UserWay config: ${response.statusText}`)
      }

      const data: UserwayConfig = await response.json()
      config.value = data

      if (data.enabled && data.account) {
        await loadUserwayWidget(data)
      }
    } catch (err) {
      console.error('Error loading UserWay config:', err)
      error.value = err instanceof Error ? err.message : 'Unknown error'
    }
  }

  const loadUserwayWidget = async (userwayConfig: UserwayConfig): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if UserWay is already loaded with the same config
      if (window.UserWayWidgetApp && isLoaded.value && 
          window.UserWayWidgetApp.account === userwayConfig.account) {
        resolve()
        return
      }

      // Complete cleanup - remove all UserWay traces
      const existingScripts = document.querySelectorAll('script[src*="userway.org"]')
      existingScripts.forEach(script => script.remove())
      
      // Remove any UserWay widget elements
      const existingWidgets = document.querySelectorAll('[data-uw-feature-tour], [data-uw-widget], .uw-widget, [id*="userway"], [class*="userway"], [data-userway]')
      existingWidgets.forEach(widget => widget.remove())
      
      // Clear all UserWay global variables
      const userWayKeys = Object.keys(window).filter(key => key.toLowerCase().includes('userway'))
      userWayKeys.forEach(key => {
        try {
          delete (window as any)[key]
        } catch (e) {
          // Ignore errors for non-configurable properties
        }
      })

      // Ensure mobile is a boolean, not a string
      const mobileValue = userwayConfig.mobile === 'true' || userwayConfig.mobile === 'True' || userwayConfig.mobile === '1'
      
      // Create the configuration object exactly as UserWay expects it
      const configObject = {
        account: userwayConfig.account,
        position: parseInt(userwayConfig.position || '5'),
        size: userwayConfig.size || 'small',
        language: userwayConfig.language || 'en-US',
        color: userwayConfig.color || '#A96428',
        type: parseInt(userwayConfig.type || '2'),
        statement_text: userwayConfig.statement_text || 'Accessibility Statement',
        statement_url: userwayConfig.statement_url || '/accessibility/',
        mobile: mobileValue
      }

      // Try alternative configuration method - use data attributes
      const script = document.createElement('script')
      script.src = `https://cdn.userway.org/widget.js?account=${configObject.account}&timestamp=${Date.now()}`
      script.async = true
      
      // Set configuration as data attributes (UserWay's preferred method)
      script.setAttribute('data-account', configObject.account || '')
      script.setAttribute('data-position', configObject.position.toString())
      script.setAttribute('data-size', configObject.size)
      script.setAttribute('data-language', configObject.language)
      script.setAttribute('data-color', configObject.color)
      script.setAttribute('data-type', configObject.type.toString())
      script.setAttribute('data-statement-text', configObject.statement_text)
      script.setAttribute('data-statement-url', configObject.statement_url)
      script.setAttribute('data-mobile', mobileValue.toString())

      // Also set the global config as fallback
      window.UserWayWidgetApp = configObject

      let hasResolved = false

      script.onload = () => {
        // Try to initialize UserWay manually if needed
        setTimeout(() => {
          // Check if UserWay object exists and try to reinitialize
          if (window.UserWay && typeof window.UserWay.reload === 'function') {
            try {
              window.UserWay.reload()
            } catch (e) {
              // Silent fail - widget will still work
            }
          }
          
          // Check for widget presence
          const widget = document.querySelector('[data-uw-widget]') || 
                        document.querySelector('.uw-widget') || 
                        document.querySelector('[id*="userway"]') ||
                        document.querySelector('[class*="uw-"]')
                        
          if (widget || window.UserWay) {
            if (!hasResolved) {
              isLoaded.value = true
              hasResolved = true
              resolve()
            }
          } else {
            if (!hasResolved) {
              isLoaded.value = true // Mark as loaded even if widget isn't visible
              hasResolved = true
              resolve()
            }
          }
        }, 3000) // Give more time for initialization
      }

      script.onerror = (errorEvent) => {
        if (!hasResolved) {
          error.value = 'Failed to load UserWay widget'
          hasResolved = true
          reject(new Error('Failed to load UserWay widget'))
        }
      }

      // Add script to head
      document.head.appendChild(script)
      
      // Extended fallback timeout
      setTimeout(() => {
        if (!hasResolved) {
          // More comprehensive check for UserWay presence
          const indicators = [
            window.UserWay,
            document.querySelector('[data-uw-widget]'),
            document.querySelector('.uw-widget'),
            document.querySelector('[id*="userway"]'),
            document.querySelector('[class*="uw-"]'),
            window.UserWayWidgetApp
          ]
          
          const found = indicators.some(indicator => indicator)
          
          if (found) {
            isLoaded.value = true
            hasResolved = true
            resolve()
          } else {
            error.value = 'UserWay initialization timeout'
            hasResolved = true
            reject(new Error('UserWay initialization timeout'))
          }
        }
      }, 25000) // Extended timeout
    })
  }

  const reloadUserway = async (locale: string = 'en'): Promise<void> => {
    // Remove existing UserWay scripts
    const existingScripts = document.querySelectorAll('script[src*="userway.org"]')
    existingScripts.forEach(script => script.remove())
    
    // Also remove any inline scripts that might contain UserWayWidgetApp
    const allScripts = document.querySelectorAll('script')
    allScripts.forEach(script => {
      if (script.innerHTML && script.innerHTML.includes('UserWayWidgetApp')) {
        script.remove()
      }
    })
    
    // Clear all UserWay-related variables
    const userWayKeys = Object.keys(window).filter(key => key.toLowerCase().includes('userway'))
    userWayKeys.forEach(key => {
      try {
        delete (window as any)[key]
      } catch (e) {
        // Ignore errors for non-configurable properties
      }
    })
    
    // Remove any existing widgets
    const existingWidgets = document.querySelectorAll('[data-uw-feature-tour], [data-uw-widget], .uw-widget, [id*="userway"], [class*="userway"], [data-userway]')
    existingWidgets.forEach(widget => widget.remove())
    
    isLoaded.value = false
    error.value = null
    
    // Reload configuration
    await loadUserwayConfig(locale)
  }

  return {
    isLoaded,
    config,
    error,
    loadUserwayConfig,
    reloadUserway
  }
} 